import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator, type StatusTone } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { formatDate } from "../components/billing";
import { RefundList, RefundRequestForm } from "../components/refunds";
import { SupportLink } from "../components/support-link";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type RefundPreviewView,
  type RefundView,
  type SafeInvoiceView,
} from "../lib/api";

/**
 * Invoice history (Phase 9, sections 33, 42).
 *
 * READ-ONLY, tenant-isolated invoice summaries. The server enforces isolation, so
 * one tenant can never read another's invoices. We show only SAFE fields — number,
 * status, date, totals, amount paid/due — and NEVER any card, bank, or payment-method
 * detail. The hosted invoice URL is fetched only on an explicit click (private,
 * no-store) and opened in a new tab; it is never rendered as durable page text.
 */

function invoiceTone(status: string, amountDueMinor: number): StatusTone {
  const s = status.toLowerCase();
  if (s === "paid") return "ok";
  if (s === "void" || s === "uncollectible") return "danger";
  if (amountDueMinor > 0 || s === "open" || s === "past_due") return "warn";
  return "neutral";
}

/**
 * A refund can only be asked for against money that actually arrived. This is the
 * gate for showing the ask at all — not an eligibility decision, which is the
 * server's to make and a person's to confirm.
 */
function wasPaid(invoice: SafeInvoiceView): boolean {
  return invoice.totals.amountPaid.minor > 0;
}

export function InvoiceRow({
  invoice,
  onOpen,
  opening,
  onRequestRefund,
  refundPending = false,
}: {
  invoice: SafeInvoiceView;
  onOpen: (invoice: SafeInvoiceView) => void;
  opening: boolean;
  onRequestRefund?: (invoice: SafeInvoiceView) => void;
  refundPending?: boolean;
}): JSX.Element {
  const due = invoice.totals.amountDue.minor > 0;
  return (
    <Card title={invoice.number ? `Invoice ${invoice.number}` : "Invoice"}>
      <div style={{ display: "grid", gap: "0.5rem" }}>
        <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
          <StatusIndicator tone={invoiceTone(invoice.status, invoice.totals.amountDue.minor)} label={invoice.status} />
          {due ? <span className="count-chip count-chip--warn">Amount due</span> : null}
        </div>
        <div className="kv-grid">
          <div className="kv">
            <span className="kv__k">Date</span>
            <span className="kv__v">{formatDate(invoice.createdAtIso)}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Total</span>
            <span className="kv__v">
              {invoice.totals.total.formatted} {invoice.currency}
            </span>
          </div>
          <div className="kv">
            <span className="kv__k">Tax</span>
            <span className="kv__v">{invoice.totals.tax.formatted}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Paid</span>
            <span className="kv__v">{invoice.totals.amountPaid.formatted}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Due</span>
            <span className="kv__v">{invoice.totals.amountDue.formatted}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Period</span>
            <span className="kv__v">
              {invoice.periodStartIso ? formatDate(invoice.periodStartIso) : "—"} –{" "}
              {invoice.periodEndIso ? formatDate(invoice.periodEndIso) : "—"}
            </span>
          </div>
        </div>
        {due ? (
          <p className="muted">
            This invoice has an amount outstanding. You can review and pay it securely in the hosted
            invoice — we never handle card details in this dashboard.
          </p>
        ) : null}
        <div className="row">
          <Button variant="secondary" onClick={() => onOpen(invoice)} disabled={opening}>
            {opening ? "Preparing…" : "View invoice"}
          </Button>
          {/* Plainly labelled and never hidden behind a support maze (Part 8.1). It
              opens a review of what a refund would mean; it does not refund anything. */}
          {onRequestRefund && wasPaid(invoice) ? (
            <Button variant="ghost" onClick={() => onRequestRefund(invoice)} disabled={refundPending}>
              {refundPending ? "Checking…" : "Request a refund"}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

export function InvoicesView({
  invoices,
  onOpen,
  openingId,
  onRequestRefund,
  refundPendingId = null,
}: {
  invoices: SafeInvoiceView[];
  onOpen: (invoice: SafeInvoiceView) => void;
  openingId: string | null;
  onRequestRefund?: (invoice: SafeInvoiceView) => void;
  refundPendingId?: string | null;
}): JSX.Element {
  if (invoices.length === 0) {
    return <EmptyState title="No invoices yet">Invoices for your subscription will appear here once they are issued.</EmptyState>;
  }
  return (
    <div className="card-grid">
      {invoices.map((invoice) => (
        <InvoiceRow
          key={invoice.id}
          invoice={invoice}
          onOpen={onOpen}
          opening={openingId === invoice.id}
          onRequestRefund={onRequestRefund}
          refundPending={refundPendingId === invoice.id}
        />
      ))}
    </div>
  );
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: SafeInvoiceView[] }
  | { kind: "unavailable"; detail?: string };

export function Invoices(): JSX.Element {
  const { tenantId, tenantName } = useAppContext();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // ── Refunds (Part 8) ──────────────────────────────────────────────────────
  // Note what is absent from this state: no amount the client decides, no payment
  // id, no destination, and no "execute" anything. The customer's session can ask
  // and can withdraw its own ask. Approving and sending are platform-operator
  // actions that this screen has no call for.
  const [refunds, setRefunds] = useState<RefundView[]>([]);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<RefundPreviewView | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
  const [withdrawErrorId, setWithdrawErrorId] = useState<string | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!apiConfigured || !tenantId) return;
    setState({ kind: "loading" });
    api.billing
      .invoices(tenantId)
      .then((res) => setState({ kind: "ready", data: res.invoices }))
      .catch((error: unknown) =>
        setState({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined }),
      );
  }, [tenantId]);

  const loadRefunds = useCallback(() => {
    if (!apiConfigured || !tenantId) return;
    // A missing refund history must never blank out the invoice page — it is the
    // secondary list on this screen, so a failure here stays quiet and empty.
    api.billing
      .refunds(tenantId)
      .then((res) => setRefunds(res.refunds))
      .catch(() => setRefunds([]));
  }, [tenantId]);

  useEffect(() => {
    load();
    loadRefunds();
  }, [load, loadRefunds]);

  const onRequestRefund = useCallback(
    async (invoice: SafeInvoiceView) => {
      if (!tenantId) return;
      setPreviewingId(invoice.id);
      setFormError(null);
      setNotice(null);
      try {
        // Consequences and the real refundable amount are fetched BEFORE the form
        // opens, so nobody commits to anything they have not been shown.
        const res = await api.billing.refundPreview(tenantId, invoice.id);
        setPreview(res.preview);
      } catch (error) {
        setPreview(null);
        setNotice(
          error instanceof ApiError
            ? error.message
            : "We could not check this payment just now. Please try again, or open a billing support conversation from the Support page and a person will help.",
        );
      } finally {
        setPreviewingId(null);
      }
    },
    [tenantId],
  );

  const onSubmitRefund = useCallback(
    async (input: { reason: string; explanation: string }) => {
      if (!tenantId || !preview) return;
      setSubmitting(true);
      setFormError(null);
      try {
        // No amount is sent. The server clamps to what is actually refundable, so a
        // tampered client cannot ask for more than was paid.
        await api.billing.requestRefund(tenantId, {
          invoiceId: preview.invoiceId,
          reason: input.reason,
          explanation: input.explanation,
        });
        setPreview(null);
        setNotice("Your request is with our billing team. Nothing has been refunded yet — we will tell you when it is.");
        loadRefunds();
      } catch (error) {
        setFormError(
          error instanceof ApiError ? error.message : "We could not send that just now. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [tenantId, preview, loadRefunds],
  );

  const onWithdrawRefund = useCallback(
    async (refund: RefundView) => {
      if (!tenantId) return;
      setWithdrawingId(refund.id);
      setWithdrawErrorId(null);
      setWithdrawError(null);
      try {
        await api.billing.withdrawRefund(tenantId, refund.id);
        loadRefunds();
      } catch (error) {
        setWithdrawErrorId(refund.id);
        setWithdrawError(
          error instanceof ApiError ? error.message : "We could not withdraw that just now. Please try again.",
        );
      } finally {
        setWithdrawingId(null);
      }
    },
    [tenantId, loadRefunds],
  );

  const onOpen = useCallback(
    async (invoice: SafeInvoiceView) => {
      if (!tenantId) return;
      setOpeningId(invoice.id);
      setNotice(null);
      try {
        // The hosted URL is fetched only at click-time (private, no-store on the server).
        const res = await api.billing.invoiceUrl(tenantId, invoice.id);
        if (res.hostedInvoiceUrl) {
          window.open(res.hostedInvoiceUrl, "_blank", "noopener,noreferrer");
        } else {
          setNotice("A hosted invoice is not available for this invoice.");
        }
      } catch (error) {
        setNotice(error instanceof ApiError ? error.message : "Could not open the invoice. Please retry.");
      } finally {
        setOpeningId(null);
      }
    },
    [tenantId],
  );

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Invoices" />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>PUBLIC_API_URL</code> when building the dashboard to view invoices.
        </NotConfiguredState>
      </>
    );
  }

  if (!tenantId) {
    return (
      <>
        <PageHeader title="Invoices" />
        <EmptyState title="Select a workspace">Choose a workspace to view its invoices.</EmptyState>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Invoices">
        <p>Billing history for {tenantName ?? "this workspace"}. We show amounts only — never card details.</p>
        {/* Part 10.2 — an invoice query used to mean composing an email and hoping.
            It now opens a conversation attached to this account, with a reference. */}
        <p className="muted">
          Something wrong on an invoice? <SupportLink topic="INVOICE">Billing Support</SupportLink> — we
          will never ask for card details there.
        </p>
      </PageHeader>
      {notice ? (
        <p role="status" className="muted">
          {notice}
        </p>
      ) : null}
      {state.kind === "loading" ? <LoadingState label="Loading invoices…" /> : null}
      {state.kind === "unavailable" ? (
        <div className="state" role="alert">
          <ApiUnavailableState detail={state.detail} />
          <Button variant="secondary" onClick={load}>
            Retry
          </Button>
        </div>
      ) : null}
      {preview ? (
        <RefundRequestForm
          preview={preview}
          onSubmit={onSubmitRefund}
          onDismiss={() => {
            setPreview(null);
            setFormError(null);
          }}
          pending={submitting}
          error={formError}
        />
      ) : null}
      {state.kind === "ready" ? (
        <InvoicesView
          invoices={state.data}
          onOpen={onOpen}
          openingId={openingId}
          onRequestRefund={onRequestRefund}
          refundPendingId={previewingId}
        />
      ) : null}
      {/*
        "Refund status", not "Refund requests" (Part 15). The list's job is to
        answer "what happened to the one I sent" — every row carries a state —
        and a customer who has already submitted one is not looking for the noun
        they clicked yesterday, they are looking for its status.
      */}
      {refunds.length > 0 ? (
        <section aria-label="Refund status" style={{ marginTop: "1.5rem" }}>
          <p className="subhead">Refund status</p>
          <RefundList
            refunds={refunds}
            onWithdraw={onWithdrawRefund}
            withdrawingId={withdrawingId}
            errorId={withdrawErrorId}
            error={withdrawError}
          />
        </section>
      ) : null}
    </>
  );
}
